import { FormEvent, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Bold, Code2, Eye, Heading2, Heading3, Image, Italic, Link as LinkIcon, List, ListOrdered, Palette, Quote, Save, Send } from "lucide-react";
import { ErrorState, LoadingState } from "../../components/LoadingState";
import { useApi } from "../../hooks/useApi";
import { apiGet, apiSend, apiUpload } from "../../services/api";
import type { Article, Author, Category, MediaLibraryItem, Tag } from "../../types";

type EditorState = {
  title: string;
  slug: string;
  subtitle: string;
  excerpt: string;
  content: string;
  custom_css: string;
  featured_image_id: string;
  author_id: string;
  category_id: string;
  status: string;
  featured: boolean;
  disclosure_label: string;
  sponsored: boolean;
  seo_title: string;
  seo_description: string;
  canonical_url: string;
  social_title: string;
  social_description: string;
  social_image_id: string;
  robots_directives: string;
  published_at: string;
  scheduled_for: string;
  tag_ids: number[];
};

const blank: EditorState = {
  title: "",
  slug: "",
  subtitle: "",
  excerpt: "",
  content: "<p></p>",
  custom_css: "",
  featured_image_id: "",
  author_id: "",
  category_id: "",
  status: "draft",
  featured: false,
  disclosure_label: "editorial",
  sponsored: false,
  seo_title: "",
  seo_description: "",
  canonical_url: "",
  social_title: "",
  social_description: "",
  social_image_id: "",
  robots_directives: "index,follow,max-image-preview:large",
  published_at: "",
  scheduled_for: "",
  tag_ids: []
};

export function ArticleEditorPage() {
  const { id } = useParams();
  const isNew = id === "new" || !id;
  const navigate = useNavigate();
  const editorRef = useRef<HTMLDivElement>(null);
  const { data: categories } = useApi<Category[]>("/api/categories", []);
  const { data: authors } = useApi<Author[]>("/api/authors", []);
  const { data: tags } = useApi<Tag[]>("/api/tags", []);
  const { data: mediaPage } = useApi<{ items: MediaLibraryItem[] }>("/api/media?pageSize=100", []);
  const { data: article, loading, error } = useApi<Article | null>(isNew ? "/api/articles/0" : `/api/articles/${id}`, [id]);
  const [state, setState] = useState<EditorState>(blank);
  const [savedArticleId, setSavedArticleId] = useState<number | null>(isNew ? null : Number(id));
  const [saveState, setSaveState] = useState<"saved" | "saving" | "dirty" | "error">("saved");
  const [preview, setPreview] = useState<"desktop" | "mobile">("desktop");
  const [uploadedMedia, setUploadedMedia] = useState<MediaLibraryItem[]>([]);
  const [createdTags, setCreatedTags] = useState<Tag[]>([]);
  const [tagInput, setTagInput] = useState("");
  const [uploading, setUploading] = useState(false);
  const [tagBusy, setTagBusy] = useState(false);
  const [assetError, setAssetError] = useState("");
  const imageInputRef = useRef<HTMLInputElement>(null);
  const inlineImageInputRef = useRef<HTMLInputElement>(null);
  const [editorMode, setEditorMode] = useState<"visual" | "html" | "css">("html");

  useEffect(() => {
    if (!article) return;
    setState({
      title: article.title,
      slug: article.slug,
      subtitle: article.subtitle ?? "",
      excerpt: article.excerpt ?? "",
      content: article.content,
      custom_css: article.custom_css ?? "",
      featured_image_id: article.image?.id ? String(article.image.id) : "",
      author_id: String(article.author.id),
      category_id: String(article.category.id),
      status: article.status,
      featured: article.featured,
      disclosure_label: article.disclosure_label,
      sponsored: article.sponsored,
      seo_title: article.seo?.title ?? "",
      seo_description: article.seo?.description ?? "",
      canonical_url: article.seo?.canonical ?? "",
      social_title: String(article.seo?.openGraph?.title ?? ""),
      social_description: String(article.seo?.openGraph?.description ?? ""),
      social_image_id: article.seo?.openGraph?.image
        ? String(mediaPage?.items.find((item) => item.url === article.seo?.openGraph?.image || new URL(item.url, window.location.origin).toString() === article.seo?.openGraph?.image)?.id ?? "")
        : "",
      robots_directives: article.seo?.robots ?? "index,follow,max-image-preview:large",
      published_at: article.published_at ? article.published_at.slice(0, 16) : "",
      scheduled_for: article.scheduled_for ? article.scheduled_for.slice(0, 16) : "",
      tag_ids: article.tags.map((tag) => tag.id)
    });
  }, [article, mediaPage]);

  const media = [...uploadedMedia, ...(mediaPage?.items ?? []).filter((item) => !uploadedMedia.some((uploaded) => uploaded.id === item.id))];
  const allTags = [...createdTags, ...(tags ?? []).filter((tag) => !createdTags.some((created) => created.id === tag.id))];

  async function uploadImage(file: File, inline = false) {
    setAssetError("");
    if (!["image/jpeg", "image/png", "image/webp", "image/avif"].includes(file.type)) {
      setAssetError("Please select a JPG, PNG, WebP or AVIF image.");
      return;
    }
    setUploading(true);
    try {
      const form = new FormData();
      form.append("file", file);
      form.append("alt_text", file.name.replace(/\.[^.]+$/, "").replace(/[-_]/g, " "));
      const result = await apiUpload<{ id: number; url: string }>("/api/media", form);
      const item = { id: result.id, url: result.url, filename: file.name, original_filename: file.name, alt_text: file.name.replace(/\.[^.]+$/, "") } as MediaLibraryItem;
      setUploadedMedia((current) => [item, ...current]);
      if (inline) {
        const escaped = result.url.replace(/&/g, "&amp;").replace(/"/g, "&quot;");
        const alt = (item.alt_text || "").replace(/&/g, "&amp;").replace(/"/g, "&quot;").replace(/</g, "&lt;");
        if (editorMode === "visual" && editorRef.current) {
          editorRef.current.focus();
          document.execCommand("insertHTML", false, `<img src="${escaped}" alt="${alt}" loading="lazy" />`);
          update("content", editorRef.current.innerHTML);
        } else {
          update("content", state.content + `\n<p><img src="${escaped}" alt="${alt}" loading="lazy" /></p>`);
        }
      } else {
        update("featured_image_id", String(result.id));
      }
    } catch (error) {
      setAssetError(error instanceof Error ? error.message : "Image upload failed.");
    } finally {
      setUploading(false);
      if (imageInputRef.current) imageInputRef.current.value = "";
      if (inlineImageInputRef.current) inlineImageInputRef.current.value = "";
    }
  }

  async function addTag() {
    const name = tagInput.trim().replace(/\s+/g, " ");
    if (!name || tagBusy) return;
    const existing = allTags.find((tag) => tag.name.toLowerCase() === name.toLowerCase());
    if (existing) {
      update("tag_ids", [...new Set([...state.tag_ids, existing.id])]);
      setTagInput("");
      return;
    }
    setTagBusy(true);
    setAssetError("");
    try {
      const tag = await apiSend<Tag>("/api/tags", { method: "POST", body: JSON.stringify({ name }) });
      setCreatedTags((current) => [...current, tag]);
      update("tag_ids", [...new Set([...state.tag_ids, tag.id])]);
      setTagInput("");
    } catch (error) {
      setAssetError(error instanceof Error ? error.message : "Unable to create tag.");
    } finally {
      setTagBusy(false);
    }
  }
  const featuredImage = media.find((item) => String(item.id) === state.featured_image_id);
  const socialImage = media.find((item) => String(item.id) === state.social_image_id) ?? featuredImage;
  const previewTitle = state.seo_title || state.title || "Untitled article";
  const previewDescription = state.seo_description || state.excerpt || state.subtitle;
  const facebookTitle = state.social_title || previewTitle;
  const facebookDescription = state.social_description || previewDescription;
  const previewUrl = state.canonical_url || `${window.location.origin}/article/${state.slug || "article-slug"}`;

  useEffect(() => {
    if (editorRef.current && editorRef.current.innerHTML !== state.content) {
      editorRef.current.innerHTML = state.content;
    }
  }, [state.content]);

  useEffect(() => {
    if (!savedArticleId || saveState !== "dirty") return;
    const handle = window.setTimeout(() => {
      saveDraft().catch(() => undefined);
    }, 8000);
    return () => window.clearTimeout(handle);
  }, [saveState, state, savedArticleId]);

  const checks = useMemo(
    () => [
      ["Title", Boolean(state.title)],
      ["Featured image", Boolean(state.featured_image_id)],
      ["Category", Boolean(state.category_id)],
      ["Author", Boolean(state.author_id)],
      ["Excerpt", Boolean(state.excerpt)],
      ["SEO description", Boolean(state.seo_description)],
      ["Content", Boolean(state.content.replace(/<[^>]+>/g, "").trim())]
    ],
    [state]
  );

  function update<K extends keyof EditorState>(key: K, value: EditorState[K]) {
    setState((current) => ({ ...current, [key]: value }));
    setSaveState("dirty");
  }

  function command(cmd: string, value?: string) {
    document.execCommand(cmd, false, value);
    update("content", editorRef.current?.innerHTML ?? "");
  }

  function payload(status = state.status) {
    return {
      ...state,
      status,
      featured_image_id: state.featured_image_id ? Number(state.featured_image_id) : null,
      social_image_id: state.social_image_id ? Number(state.social_image_id) : null,
      author_id: Number(state.author_id || authors?.[0]?.id),
      category_id: Number(state.category_id || categories?.[0]?.id),
      published_at: state.published_at ? new Date(state.published_at).toISOString() : null,
      scheduled_for: state.scheduled_for ? new Date(state.scheduled_for).toISOString() : null
    };
  }

  async function saveDraft() {
    setSaveState("saving");
    try {
      if (savedArticleId) {
        const updated = await apiSend<Article>(`/api/articles/${savedArticleId}`, {
          method: "PATCH",
          body: JSON.stringify(payload())
        });
        setState((current) => ({ ...current, slug: updated.slug }));
        setSaveState("saved");
        return updated.id;
      } else {
        const created = await apiSend<Article>("/api/articles", {
          method: "POST",
          body: JSON.stringify(payload("draft"))
        });
        setSavedArticleId(created.id);
        navigate(`/admin/articles/${created.id}/edit`, { replace: true });
        setSaveState("saved");
        return created.id;
      }
    } catch {
      setSaveState("error");
      return null;
    }
  }

  async function submit(event: FormEvent) {
    event.preventDefault();
    await saveDraft();
  }

  async function publish(status: "review" | "scheduled" | "published") {
    const articleId = (await saveDraft()) ?? savedArticleId ?? Number(id);
    if (!articleId) return;
    await apiSend(`/api/articles/${articleId}/status`, {
      method: "POST",
      body: JSON.stringify({
        status,
        publishAt: status === "scheduled" ? new Date(state.scheduled_for).toISOString() : undefined
      })
    });
    navigate("/admin/articles");
  }

  if (!isNew && loading) return <LoadingState label="Loading editor" />;
  if (!isNew && error) return <ErrorState message={error} />;

  return (
    <form className="admin-page editor-page newsroom-editor" onSubmit={submit}>
      <header className="admin-page-header">
        <div>
          <h1>{isNew ? "New Article" : "Edit Article"}</h1>
          <p aria-live="polite">{saveState === "saving" ? "Saving..." : saveState === "dirty" ? "Unsaved changes" : saveState === "error" ? "Save failed" : "Saved"}</p>
        </div>
        <div className="header-actions">
          <button className="button ghost" type="submit">
            <Save size={16} />
            Save
          </button>
          <button className="button ghost" type="button" onClick={() => publish("review")}>
            Send to Review
          </button>
          <button className="button" type="button" onClick={() => publish(state.scheduled_for ? "scheduled" : "published")}>
            <Send size={16} />
            {state.scheduled_for ? "Schedule" : "Publish"}
          </button>
        </div>
      </header>

      <p className="newsroom-editor-intro">Create, design and publish your story. Images and tags can be added without leaving the editor.</p>
      {assetError && <p className="newsroom-editor-error" role="alert">{assetError}</p>}
      <input ref={imageInputRef} type="file" accept="image/jpeg,image/png,image/webp,image/avif" hidden onChange={(event) => { const file = event.target.files?.[0]; if (file) void uploadImage(file); }} />
      <input ref={inlineImageInputRef} type="file" accept="image/jpeg,image/png,image/webp,image/avif" hidden onChange={(event) => { const file = event.target.files?.[0]; if (file) void uploadImage(file, true); }} />
      <div className="editor-grid">
        <section className="admin-panel editor-main">
          <div className="newsroom-section-label">Story details</div>
          <label>
            Title
            <input value={state.title} onChange={(event) => update("title", event.target.value)} required />
          </label>
          <label>
            Subtitle / dek
            <input value={state.subtitle} onChange={(event) => update("subtitle", event.target.value)} />
          </label>
          <label>
            Excerpt
            <textarea rows={3} value={state.excerpt} onChange={(event) => update("excerpt", event.target.value)} />
          </label>
          <div className="newsroom-section-label">Article body</div>
          <div className="editor-mode-tabs segmented" aria-label="Article editor mode">
            <button type="button" className={editorMode === "visual" ? "active" : ""} onClick={() => setEditorMode("visual")}>
              <Eye size={15} /> Visual
            </button>
            <button type="button" className={editorMode === "html" ? "active" : ""} onClick={() => setEditorMode("html")}>
              <Code2 size={15} /> HTML
            </button>
            <button type="button" className={editorMode === "css" ? "active" : ""} onClick={() => setEditorMode("css")}>
              <Palette size={15} /> CSS
            </button>
          </div>
          {editorMode === "visual" && <div className="editor-toolbar" aria-label="Rich text toolbar">
            <button type="button" title="H2" onClick={() => command("formatBlock", "h2")}>
              <Heading2 size={16} />
            </button>
            <button type="button" title="H3" onClick={() => command("formatBlock", "h3")}>
              <Heading3 size={16} />
            </button>
            <button type="button" title="Bold" onClick={() => command("bold")}>
              <Bold size={16} />
            </button>
            <button type="button" title="Italic" onClick={() => command("italic")}>
              <Italic size={16} />
            </button>
            <button type="button" title="Link" onClick={() => command("createLink", prompt("URL") || "")}>
              <LinkIcon size={16} />
            </button>
            <button type="button" title="Bulleted list" onClick={() => command("insertUnorderedList")}>
              <List size={16} />
            </button>
            <button type="button" title="Numbered list" onClick={() => command("insertOrderedList")}>
              <ListOrdered size={16} />
            </button>
            <button type="button" title="Quote" onClick={() => command("formatBlock", "blockquote")}>
              <Quote size={16} />
            </button>
            <button type="button" title="Horizontal rule" onClick={() => command("insertHorizontalRule")}>
              HR
            </button>
            <button type="button" title="Upload image" disabled={uploading} onClick={() => inlineImageInputRef.current?.click()}>
              <Image size={16} /> Upload
            </button>
            <button type="button" title="Image URL" onClick={() => command("insertImage", prompt("Image URL") || "")}>
              <Image size={16} />
            </button>
          </div>}
          {editorMode === "visual" && (
            <div
              ref={editorRef}
              className="rich-editor"
              contentEditable
              suppressContentEditableWarning
              role="textbox"
              aria-label="Article visual content editor"
              onInput={() => update("content", editorRef.current?.innerHTML ?? "")}
            />
          )}
          {editorMode === "html" && (
            <textarea
              className="source-editor"
              value={state.content}
              onChange={(event) => update("content", event.target.value)}
              spellCheck={false}
              aria-label="Article HTML source"
            />
          )}
          {editorMode === "css" && (
            <textarea
              className="source-editor css-source-editor"
              value={state.custom_css}
              onChange={(event) => update("custom_css", event.target.value)}
              spellCheck={false}
              placeholder={".lead { font-size: 1.2rem; }\n.callout { border-left: 4px solid #c99a2e; padding: 1rem; }"}
              aria-label="Optional article CSS"
            />
          )}
        </section>

        <aside className="admin-panel editor-sidebar">
          <div className="newsroom-section-label">Publishing settings</div>
          <label>
            Slug
            <input value={state.slug} onChange={(event) => update("slug", event.target.value)} />
          </label>
          <label>
            Category
            <select value={state.category_id} onChange={(event) => update("category_id", event.target.value)}>
              <option value="">Select category</option>
              {categories?.map((category) => (
                <option key={category.id} value={category.id}>
                  {category.name}
                </option>
              ))}
            </select>
          </label>
          <label>
            Author
            <select value={state.author_id} onChange={(event) => update("author_id", event.target.value)}>
              <option value="">Select author</option>
              {authors?.map((author) => (
                <option key={author.id} value={author.id}>
                  {author.name}
                </option>
              ))}
            </select>
          </label>
          <div className="newsroom-section-label">Featured image</div>
          <div className="newsroom-upload-zone" onDragOver={(event) => event.preventDefault()} onDrop={(event) => { event.preventDefault(); const file = event.dataTransfer.files?.[0]; if (file) void uploadImage(file); }}>
            <Image size={22} aria-hidden="true" />
            <p>Drag an image here, or upload from your device</p>
            <button type="button" className="button ghost" disabled={uploading} onClick={() => imageInputRef.current?.click()}>{uploading ? "Uploading…" : "Upload featured image"}</button>
            <small>JPG, PNG, WebP or AVIF</small>
          </div>
          <label>
            Choose from media library
            <select value={state.featured_image_id} onChange={(event) => update("featured_image_id", event.target.value)}>
              <option value="">No featured image</option>
              {media.map((item) => <option key={item.id} value={item.id}>{item.alt_text || item.original_filename || item.filename}</option>)}
            </select>
          </label>
          {featuredImage ? <img className="editor-selected-image" src={featuredImage.url} alt={featuredImage.alt_text || "Selected featured image"} /> : null}
          <label>
            Scheduled date and time
            <input type="datetime-local" value={state.scheduled_for} onChange={(event) => update("scheduled_for", event.target.value)} />
          </label>
          <label>
            Disclosure
            <select value={state.disclosure_label} onChange={(event) => update("disclosure_label", event.target.value)}>
              <option value="editorial">Editorial</option>
              <option value="sponsored">Sponsored</option>
              <option value="partner_content">Partner Content</option>
              <option value="affiliate">Affiliate</option>
            </select>
          </label>
          <label className="checkbox-row">
            <input type="checkbox" checked={state.featured} onChange={(event) => update("featured", event.target.checked)} />
            Featured
          </label>
          <div className="newsroom-section-label">Tags</div>
          <div className="newsroom-tags" aria-label="Selected tags">
            {state.tag_ids.map((tagId) => {
              const tag = allTags.find((item) => item.id === tagId);
              return <span key={tagId} className="newsroom-tag-chip">{tag?.name ?? `Tag #${tagId}`}<button type="button" aria-label={`Remove ${tag?.name ?? "tag"}`} onClick={() => update("tag_ids", state.tag_ids.filter((id) => id !== tagId))}>×</button></span>;
            })}
          </div>
          <div className="newsroom-tag-entry">
            <input aria-label="New or existing tag" value={tagInput} onChange={(event) => setTagInput(event.target.value)} onKeyDown={(event) => { if (event.key === "Enter") { event.preventDefault(); void addTag(); } }} placeholder="Type a tag and press Enter" />
            <button type="button" className="button ghost" disabled={tagBusy || !tagInput.trim()} onClick={() => void addTag()}>{tagBusy ? "Adding…" : "Add"}</button>
          </div>
          <div className="newsroom-tag-suggestions">
            {allTags.filter((tag) => !state.tag_ids.includes(tag.id) && (!tagInput || tag.name.toLowerCase().includes(tagInput.toLowerCase()))).slice(0,8).map((tag) => (
              <button type="button" key={tag.id} onClick={() => { update("tag_ids", [...state.tag_ids, tag.id]); setTagInput(""); }}>+ {tag.name}</button>
            ))}
          </div>
          <div className="newsroom-section-label">Search & social</div>
          <label>
            Google title ({state.seo_title.length}/60)
            <input maxLength={255} value={state.seo_title} onChange={(event) => update("seo_title", event.target.value)} placeholder={state.title || "Search result title"} />
          </label>
          <label>
            Google description ({state.seo_description.length}/160)
            <textarea rows={3} maxLength={320} value={state.seo_description} onChange={(event) => update("seo_description", event.target.value)} placeholder={state.excerpt || "Search result description"} />
          </label>
          <label>
            Canonical URL
            <input type="url" value={state.canonical_url} onChange={(event) => update("canonical_url", event.target.value)} placeholder={`${window.location.origin}/article/article-slug`} />
          </label>
          <label>
            Google indexing
            <select value={state.robots_directives} onChange={(event) => update("robots_directives", event.target.value)}>
              <option value="index,follow,max-image-preview:large">Index and follow</option>
              <option value="noindex,follow">Do not index</option>
              <option value="noindex,nofollow">Private from search</option>
            </select>
          </label>
          <label>
            Facebook title ({state.social_title.length}/65)
            <input maxLength={255} value={state.social_title} onChange={(event) => update("social_title", event.target.value)} placeholder={previewTitle} />
          </label>
          <label>
            Facebook description ({state.social_description.length}/200)
            <textarea rows={3} maxLength={320} value={state.social_description} onChange={(event) => update("social_description", event.target.value)} placeholder={previewDescription} />
          </label>
          <label>
            Facebook preview image
            <select value={state.social_image_id} onChange={(event) => update("social_image_id", event.target.value)}>
              <option value="">Use featured image</option>
              {media.map((item) => <option key={item.id} value={item.id}>{item.alt_text || item.original_filename || item.filename}</option>)}
            </select>
          </label>
          <div className="checklist">
            <h2>Publishing Checklist</h2>
            {checks.map(([label, good]) => (
              <span key={String(label)} className={good ? "check-good" : "check-warn"}>
                {good ? "OK" : "Needs"} {label}
              </span>
            ))}
          </div>
        </aside>
      </div>

      <section className="admin-panel preview-panel">
        <div className="section-heading">
          <h2>Preview</h2>
          <div className="segmented">
            <button type="button" className={preview === "desktop" ? "active" : ""} onClick={() => setPreview("desktop")}>
              <Eye size={15} />
              Desktop
            </button>
            <button type="button" className={preview === "mobile" ? "active" : ""} onClick={() => setPreview("mobile")}>
              Mobile
            </button>
          </div>
        </div>
        <div className={`preview-frame preview-${preview}`}>
          {state.custom_css ? <style>{`@scope (.article-custom-preview) { ${state.custom_css} }`}</style> : null}
          <h1>{state.title || "Untitled article"}</h1>
          <p>{state.subtitle}</p>
          <div className="article-custom-preview" dangerouslySetInnerHTML={{ __html: state.content }} />
        </div>
        {savedArticleId ? <Link to={`/article/${state.slug}`} target="_blank">Open public preview</Link> : null}
      </section>

      <section className="admin-panel metadata-previews">
        <div>
          <h2>Google Preview</h2>
          <div className="google-result-preview">
            <div className="google-result-site">ZimSmart</div>
            <div className="google-result-url">{previewUrl}</div>
            <div className="google-result-title">{previewTitle}</div>
            <p>{previewDescription || "Add an excerpt or Google description to preview the search result."}</p>
          </div>
        </div>
        <div>
          <h2>Facebook Preview</h2>
          <div className="facebook-card-preview">
            {socialImage ? <img src={socialImage.url} alt="Facebook link preview" /> : <div className="facebook-image-empty">Select a preview image</div>}
            <div className="facebook-card-copy">
              <small>ZIMSMART.CO.ZW</small>
              <strong>{facebookTitle}</strong>
              <p>{facebookDescription || "Add a social description to preview the shared post."}</p>
            </div>
          </div>
        </div>
      </section>
    </form>
  );
}
